import axios from "axios";

import env from "../Config/env";
import authStorage from "./authStorage";
import { refreshAccessToken, handleLogoff } from "./authRefresh";
import { decideOn401, isAuthEndpoint } from "./authPolicy";
import logger from "./logger";

/**
 * Cliente HTTP central do Dream4 Webapp.
 *
 * Auth Fase 2: o access token é curto (30min) e vive em memória. Este cliente é
 * o ÚNICO lugar que injeta o token e trata o refresh — por isso os thunks devem
 * passar por ele (em vez de `axios` direto).
 *
 * - request: injeta `Authorization: Bearer <access>` (+ `x-access-token` como
 *   rede de segurança; o backend aceita ambos).
 * - response: em 401, decide por `code`:
 *     TOKEN_EXPIRED/TOKEN_STALE → silent-refresh (fila single-flight) + repetir;
 *     SESSION_REVOKED → logoff; AUTH_REQUIRED/INVALID_TOKEN → login.
 *   **Gating:** refresh/logoff só agem se houver refresh token (modelo novo).
 *   Antes da virada (Fase B), sem refresh token, comporta-se como hoje (rejeita).
 *
 * Origem: TD-004 + Auth Fase 2.
 */

const api = axios.create({
	baseURL: env.API_URL,
	timeout: 30000,
});

api.interceptors.request.use(
	(config) => {
		const token = authStorage.getAccessToken();
		if (token) {
			config.headers = config.headers || {};
			const setH = (k, v) => { if (typeof config.headers.set === "function") config.headers.set(k, v, true); else config.headers[k] = v; };
			const delH = (k) => { if (typeof config.headers.delete === "function") config.headers.delete(k); else delete config.headers[k]; };
			if (authStorage.hasRefreshToken()) {
				// Modelo novo (Fase 2): SÓ Bearer (caminho moderno do validateRequest).
				// Remove x-access-token mesmo se um thunk legado o tiver setado — assim o
				// access novo não vai pelo caminho legado (exp em ms — TD-002), que o rejeitaria.
				setH("Authorization", `Bearer ${token}`);
				delH("x-access-token");
			} else {
				// Legado (antes da virada): só x-access-token, como hoje.
				if (!config.headers["x-access-token"]) setH("x-access-token", token);
			}
		}
		return config;
	},
	(error) => Promise.reject(error)
);

api.interceptors.response.use(
	(response) => response,
	async (error) => {
		// AbortController-cancelado: deixa o caller decidir (Services/abortError.js).
		if (axios.isCancel(error)) return Promise.reject(error);

		const status = error?.response?.status;
		const code = error?.response?.data?.code;
		const original = error?.config || {};

		if (status === 401 && !original._authRetry && !isAuthEndpoint(original.url)) {
			const action = decideOn401(code);

			// Silent-refresh + repetir (só quando há refresh token = modelo novo).
			if (action === "refresh" && authStorage.hasRefreshToken()) {
				try {
					await refreshAccessToken(); // single-flight; persiste o novo access
					original._authRetry = true;
					// Headers são reinjetados pelo request interceptor (Bearer fresco
					// + remoção de x-access-token) ao re-executar api(original).
					return api(original);
				} catch (e) {
					logger.warn("[api] refresh falhou — logoff", e?.message);
					handleLogoff();
					return Promise.reject(e);
				}
			}

			// SESSION_REVOKED / INVALID_TOKEN / AUTH_REQUIRED.
			// Gated: só desloga "de verdade" no modelo novo (com refresh token).
			// Antes da virada, mantém o comportamento atual (apenas rejeita).
			if (authStorage.hasRefreshToken()) {
				logger.warn(`[api] 401 (${code || "sem code"}) → logoff`);
				handleLogoff();
			} else {
				logger.warn("[api] 401 unauthorized — sessão pode ter expirado");
			}
			return Promise.reject(error);
		}

		if (status >= 500) {
			logger.error("[api] 5xx error", { url: original.url, status, message: error?.message });
		}

		return Promise.reject(error);
	}
);

export default api;
